import React from 'react'
import styled from 'styled-components'
import onClickOutside from 'react-onclickoutside'

import { palette, fonts, mediaQueries, shadows } from '../../styles/variables'
import { translate } from '../../utils/translation'
import Icon, { ArrowIcon, DomainIcon } from '../icon/Icon'
import plusIcon from '../../styles/assets/plus.svg'
import { getMaxValuesForUser } from '../../utils/freeVersionBlock'
import { Text } from '../topography'

const ProjectSelectorWrapper = styled.div((props) => ({
  position: 'relative',
  marginLeft: '10px',
  cursor: 'pointer',
  zIndex: 103,
  background: palette.white,
  [mediaQueries.mobile]: {
    fontSize: '20px',
    margin: 0,
  },
  ...props.overridingStyle,
}))

const SelectedProject = styled.div((props) => ({
  display: 'flex',
  alignItems: 'center',
  width: props.fullWidth ? '100%' : '224px',
  height: '40px',
  border: `1px solid ${
    props.open || props.selected ? palette.orange : palette.lightGray
  }`,
  borderRadius: '2px',
  padding: '0 20px',
  justifyContent: 'space-between',
  backgroundColor: props.selected ? palette.orange : 'inherit',
  [mediaQueries.mobile]: {
    width: '100%',
  },
}))

const ProjectWrapper = styled.div((props) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '13px 20px',
  background: props.isSelected ? palette.lightGray20 : 'inherit',
  minWidth: '224px',
  '&:hover': {
    background: palette.lightGray20,
  },
  '&:hover div': {
    color: palette.orange,
  },
}))

const ProjectTitle = styled.div((props) => ({
  fontSize: '15px',
  whiteSpace: 'nowrap',
  color: props.isSelected ? palette.orange : 'inherit',
  fontWeight: props.isSelected ? 'bold' : 'inherit',
}))

const ProjectDomain = styled.span({
  marginTop: '5px',
  fontSize: '14px',
  whiteSpace: 'nowrap',
  fontFamily: fonts.primaryLight,
  color: palette.darkGray50,
})

const ProjectList = styled.div((props) => ({
  position: 'absolute',
  zIndex: 100,
  background: palette.white,
  boxShadow: shadows.shadowA,
  width: props.fullWidth ? '100%' : 'auto',
  marginTop: '4px',
  maxHeight: props.smallBox ? '175px' : '280px',
  overflow: 'auto',

  /* width */
  '::-webkit-scrollbar': {
    width: '4px',
  },

  /* Track */
  '::-webkit-scrollbar-track': {
    background: palette.lightGray,
  },

  /* Handle */
  '::-webkit-scrollbar-thumb': {
    width: '8px',
    background: palette.gray,
  },

  /* Handle on hover */
  '::-webkit-scrollbar-thumb:hover': {
    background: palette.darkGray50,
  },

  [mediaQueries.mobile]: {
    [props.align]: 0,
    width: '100%',
  },
}))

const AddButton = styled.div({
  height: '54px',
  display: 'flex',
  alignItems: 'center',
  borderTop: `1px solid ${palette.lightGray}`,
  color: palette.darkGray,
  fontFamily: fonts.primaryBook,
  fontSize: '14px',
  lineHeight: '16px',
  padding: '12px 16px',
})

const Project = ({ onClick, isSelected, icon, domain, name, index }) => (
  <ProjectWrapper onClick={onClick} isSelected={isSelected}>
    {icon || (
      <DomainIcon colorIndex={index} style={{ margin: '0 16px 0 0' }}>
        {domain ? domain.charAt(0) : name.charAt(0)}
      </DomainIcon>
    )}

    <div>
      <ProjectTitle isSelected={isSelected}>{name}</ProjectTitle>
      <ProjectDomain>{domain}</ProjectDomain>
    </div>
  </ProjectWrapper>
)

class ProjectSelector extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      isOpen: false,
      index: 0,
    }
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  handleClickOutside = () => {
    this.setState({ isOpen: false })
  }

  openList = () => {
    this.setState((prev) => ({ isOpen: !prev.isOpen }))
  }

  handleHover = () => {
    this.setState({ isOpen: true })
  }

  selectProject = (project, index) => {
    this.setState({ isOpen: false, index })
    this.props.onChangeProject(project)
  }

  addProject = () => {
    this.setState({ isOpen: false })
    this.props.onAddProject()
  }

  render() {
    const { isOpen, index } = this.state
    const {
      projects,
      selectedProject,
      selected,
      hoverEnabled,
      fullWidth,
      smallBox,
      style,
      showAllProjects,
      hideAddButton,
      align,
    } = this.props
    const { MAX_PROJECTS_PER_USER } = getMaxValuesForUser()
    return (
      <ProjectSelectorWrapper
        overridingStyle={style}
        data-testid="project-selector-wrapper"
      >
        <SelectedProject
          onClick={this.openList}
          open={isOpen}
          fullWidth={fullWidth}
          selected={selected || (isOpen && hoverEnabled)}
          onMouseEnter={() => {
            if (hoverEnabled) this.handleHover()
          }}
        >
          {selectedProject && (
            <Text
              lineHeight="16px"
              style={{
                display: 'inline-block',
                textAlign: 'left',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                color: selected ? palette.white : undefined,
              }}
            >
              {selectedProject.icon
                ? selectedProject.icon
                : selectedProject?.domain && (
                    <DomainIcon
                      colorIndex={index}
                      style={{ margin: '0 16px 0 0' }}
                    >
                      {selectedProject.domain.charAt(0)}
                    </DomainIcon>
                  )}
              {selectedProject.title}
              {hoverEnabled && ` | ${translate('project')}`}
            </Text>
          )}
          {!selectedProject && (showAllProjects || hoverEnabled) && (
            <Text
              lineHeight="16px"
              style={{
                display: 'flex',
                alignItems: 'center',
                textOverflow: 'ellipsis',
                overflow: 'auto',
                whiteSpace: 'nowrap',
                color: isOpen && hoverEnabled ? palette.white : undefined,
              }}
            >
              {translate(hoverEnabled ? 'projects' : 'all_projects')}
            </Text>
          )}
          {isOpen ? (
            <ArrowIcon
              up
              color={
                selected || hoverEnabled ? palette.white : palette.darkGray
              }
              style={{ marginRight: '10px' }}
            />
          ) : (
            <ArrowIcon
              down
              color={selected ? palette.white : palette.darkGray}
              style={{ marginRight: '10px' }}
            />
          )}
        </SelectedProject>
        {isOpen && (
          <ProjectList
            data-testid="project-selector-wrapper-open"
            align={align}
            fullWidth={fullWidth}
            smallBox={smallBox}
            onMouseLeave={() => {
              if (hoverEnabled) this.openList()
            }}
          >
            {showAllProjects && (
              <ProjectWrapper onClick={() => this.selectProject(null, -1)}>
                <ProjectTitle>{translate('all_projects')}</ProjectTitle>
              </ProjectWrapper>
            )}
            {projects.map((project, indexItem) => (
              <Project
                index={indexItem}
                isSelected={project.id === selectedProject?.id}
                key={project.id}
                name={project.title}
                domain={project.domain}
                icon={project.icon}
                onClick={() => this.selectProject(project, indexItem)}
              />
            ))}
            {!hideAddButton && (
              <AddButton onClick={this.addProject}>
                <Icon src={plusIcon} margin="0 12px 0 0" />
                {translate('add_new_project')}
                <span style={{ fontFamily: fonts.primary, marginLeft: '4px' }}>
                  {`(${projects.length}/${MAX_PROJECTS_PER_USER})`}
                </span>
              </AddButton>
            )}
          </ProjectList>
        )}
      </ProjectSelectorWrapper>
    )
  }
}

export default onClickOutside(ProjectSelector)
